import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';

interface ConfirmEmailChangeDialogProps {
  open: boolean;
  currentEmail: string;
  newEmail: string;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * An email is what somebody signs in with, so changing it changes who can reach that account. That makes
 * it an access-control action wearing the clothes of a text field, and it gets confirmed before it saves.
 */
const ConfirmEmailChangeDialog = ({
  open,
  currentEmail,
  newEmail,
  isPending,
  onCancel,
  onConfirm,
}: ConfirmEmailChangeDialogProps): ReactElement => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('memberDetails.emailChange.title')}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-foreground">{t('memberDetails.emailChange.body', { currentEmail, newEmail })}</p>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
            {t('memberDetails.emailChange.cancel')}
          </Button>
          <Button type="button" onClick={onConfirm} disabled={isPending}>
            {t('memberDetails.emailChange.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmEmailChangeDialog;
